import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { API } from "../lib/api";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { useAuth } from "../contexts/AuthContext";
import Avatar from "./Avatar";
import {
  X, Calendar, Tag, CheckSquare, Square, Trash2, Sparkles,
  MessageCircle, Plus, AlertCircle, Loader2, Paperclip, Download,
  FileText, Image as ImageIcon,
} from "lucide-react";

const PRIORITIES = ["low", "medium", "high", "urgent"];
const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_MB = 25;
const IMAGE_EXTS = new Set(["jpg", "jpeg", "png", "webp", "gif", "heic", "heif"]);

function _fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function _extOf(name) {
  const m = /\.([^.]+)$/.exec(name || "");
  return m ? m[1].toLowerCase() : "";
}

export default function TaskDetailPanel({ taskId, onClose, onDeleted, onUpdated }) {
  const { members, memberById } = useWorkspace();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [subtasks, setSubtasks] = useState([]);
  const [comment, setComment] = useState("");
  const [newSub, setNewSub] = useState("");
  const [newCheck, setNewCheck] = useState("");
  const [aiSummary, setAiSummary] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [attachUploading, setAttachUploading] = useState(false);
  const [attachError, setAttachError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!taskId) return;
    api.get(`/projects/`).catch(() => {}); // noop just to keep ref
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const refresh = async () => {
    const tasksRes = await api.get(`/projects/$_/tasks`).catch(() => null); // ignore
    void tasksRes;
    // We fetch task by listing tasks of project — but we don't know project_id yet.
    // Simpler: use update endpoint or a dedicated GET. We'll fetch via comments + tasks API:
    const cs = await api.get(`/tasks/${taskId}/comments`);
    setComments(cs.data);
    // We rely on parent providing the loaded task object. But we need fresh — use PATCH no-op? Avoid.
    // Use an internal endpoint: GET /tasks/{id}. We don't have it - use PATCH with empty body to get fresh task back? Yes our PATCH returns task.
    const t = await api.patch(`/tasks/${taskId}`, {});
    setTask(t.data);
    // subtasks: fetch project tasks then filter
    const allTasks = await api.get(`/projects/${t.data.project_id}/tasks`);
    setSubtasks(allTasks.data.filter((x) => x.parent_task_id === taskId));
  };

  if (!taskId) return null;
  if (!task) {
    return (
      <div className="fixed inset-0 z-40 flex justify-end" data-testid="task-panel-loading">
        <div className="absolute inset-0 bg-black/60" onClick={onClose} />
        <div className="relative w-[560px] max-w-full h-full bg-[#0B0B0E] border-l border-zinc-800 p-6 text-zinc-500">Loading…</div>
      </div>
    );
  }

  const update = async (patch) => {
    const r = await api.patch(`/tasks/${taskId}`, patch);
    setTask(r.data);
    onUpdated && onUpdated(r.data);
  };

  const toggleStatus = () => update({ status: task.status === "done" ? "open" : "done" });

  const addComment = async () => {
    if (!comment.trim()) return;
    await api.post(`/tasks/${taskId}/comments`, { text: comment, mentions: [] });
    setComment("");
    const cs = await api.get(`/tasks/${taskId}/comments`);
    setComments(cs.data);
  };

  const addSubtask = async () => {
    if (!newSub.trim()) return;
    await api.post(`/tasks`, {
      project_id: task.project_id,
      tasklist_id: task.tasklist_id,
      title: newSub,
      parent_task_id: taskId,
    });
    setNewSub("");
    refresh();
  };

  const addChecklistItem = async () => {
    if (!newCheck.trim()) return;
    const next = [...(task.checklist || []), { id: crypto.randomUUID(), text: newCheck, done: false }];
    await update({ checklist: next });
    setNewCheck("");
  };

  const toggleCheck = async (id) => {
    const next = (task.checklist || []).map((c) => c.id === id ? { ...c, done: !c.done } : c);
    await update({ checklist: next });
  };

  const removeCheck = async (id) => {
    const next = (task.checklist || []).filter((c) => c.id !== id);
    await update({ checklist: next });
  };

  const toggleAssignee = async (uid) => {
    const cur = task.assignees || [];
    const next = cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid];
    await update({ assignees: next });
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this task and all subtasks?")) return;
    await api.delete(`/tasks/${taskId}`);
    onDeleted && onDeleted(taskId);
    onClose();
  };

  const generateSummary = async () => {
    setAiLoading(true);
    try {
      const r = await api.post(`/ai/summarize-task`, { task_id: taskId });
      setAiSummary(r.data.summary);
    } finally {
      setAiLoading(false);
    }
  };

  const pickFile = () => {
    setAttachError("");
    fileInputRef.current?.click();
  };

  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setAttachError("");
    if (file.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
      setAttachError(`File exceeds ${MAX_ATTACHMENT_MB} MB`);
      return;
    }
    if ((task.attachments || []).length >= MAX_ATTACHMENTS) {
      setAttachError(`Max ${MAX_ATTACHMENTS} attachments per task`);
      return;
    }
    setAttachUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(`/tasks/${taskId}/attachments`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const next = { ...task, attachments: [...(task.attachments || []), r.data] };
      setTask(next);
      onUpdated && onUpdated(next);
    } catch (err) {
      setAttachError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setAttachUploading(false);
    }
  };

  const removeAttachment = async (attId) => {
    if (!window.confirm("Delete this attachment?")) return;
    try {
      await api.delete(`/tasks/${taskId}/attachments/${attId}`);
      const next = {
        ...task,
        attachments: (task.attachments || []).filter((a) => a.id !== attId),
      };
      setTask(next);
      onUpdated && onUpdated(next);
    } catch (err) {
      setAttachError(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="task-detail-panel">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-[640px] max-w-full h-full bg-[#0B0B0E] border-l border-zinc-800 overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-zinc-800">
          <div className="flex items-start gap-3 flex-1">
            <button
              onClick={toggleStatus}
              data-testid="panel-task-toggle"
              className={`mt-1 ${task.status === "done" ? "text-[#10B981]" : "text-zinc-500 hover:text-[#D4A968]"}`}
            >
              {task.status === "done" ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
            </button>
            <input
              value={task.title}
              onChange={(e) => setTask({ ...task, title: e.target.value })}
              onBlur={() => update({ title: task.title })}
              data-testid="panel-task-title"
              className={`flex-1 bg-transparent text-xl font-bold text-white outline-none border-b border-transparent focus:border-zinc-700 ${
                task.status === "done" ? "line-through text-zinc-500" : ""
              }`}
            />
          </div>
          <div className="flex items-center gap-1 ml-2">
            <button onClick={onDelete} data-testid="panel-delete" className="p-1.5 text-zinc-500 hover:text-[#EF4444] rounded hover:bg-[#1B1B1F]">
              <Trash2 className="w-4 h-4" />
            </button>
            <button onClick={onClose} data-testid="panel-close" className="p-1.5 text-zinc-500 hover:text-white rounded hover:bg-[#1B1B1F]">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Meta row */}
        <div className="px-5 py-3 border-b border-zinc-800 grid grid-cols-2 gap-3 text-xs">
          <div>
            <label className="text-[10px] uppercase font-mono tracking-widest text-zinc-500">Priority</label>
            <select
              value={task.priority}
              onChange={(e) => update({ priority: e.target.value })}
              data-testid="panel-priority"
              className="w-full mt-1 bg-[#141417] border border-zinc-800 px-2 py-1.5 text-white rounded outline-none"
            >
              {PRIORITIES.map((p) => <option key={p} value={p}>{p.toUpperCase()}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase font-mono tracking-widest text-zinc-500">Due date</label>
            <input
              type="date"
              value={task.due_date || ""}
              onChange={(e) => update({ due_date: e.target.value || null })}
              data-testid="panel-due-date"
              className="w-full mt-1 bg-[#141417] border border-zinc-800 px-2 py-1.5 text-white rounded outline-none font-mono"
            />
          </div>
        </div>

        {/* Assignees */}
        <div className="px-5 py-4 border-b border-zinc-800">
          <div className="text-[10px] uppercase font-mono tracking-widest text-zinc-500 mb-2">Assignees</div>
          <div className="flex flex-wrap gap-1.5" data-testid="panel-assignees">
            {members.map((m) => {
              const active = (task.assignees || []).includes(m.id);
              return (
                <button
                  key={m.id}
                  onClick={() => toggleAssignee(m.id)}
                  data-testid={`assignee-${m.id}`}
                  className={`flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full border text-xs transition-colors ${
                    active ? "bg-[#D4A968]/10 border-[#D4A968]/40 text-white" : "bg-[#141417] border-zinc-800 text-zinc-400 hover:border-zinc-600"
                  }`}
                >
                  <Avatar user={m} size="xs" />
                  <span>{m.name.split(" ")[0]}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Description */}
        <div className="px-5 py-4 border-b border-zinc-800">
          <div className="text-[10px] uppercase font-mono tracking-widest text-zinc-500 mb-2">Description</div>
          <textarea
            value={task.description || ""}
            onChange={(e) => setTask({ ...task, description: e.target.value })}
            onBlur={() => update({ description: task.description })}
            data-testid="panel-description"
            rows={3}
            placeholder="Add a description…"
            className="w-full bg-[#141417] border border-zinc-800 focus:border-zinc-600 rounded p-2.5 text-sm text-white outline-none placeholder:text-zinc-600 resize-none"
          />
        </div>

        {/* Checklist */}
        <div className="px-5 py-4 border-b border-zinc-800">
          <div className="text-[10px] uppercase font-mono tracking-widest text-zinc-500 mb-2">Checklist</div>
          <div className="space-y-1.5">
            {(task.checklist || []).map((c) => (
              <div key={c.id} data-testid={`checklist-${c.id}`} className="flex items-center gap-2 group">
                <button onClick={() => toggleCheck(c.id)} className={c.done ? "text-[#10B981]" : "text-zinc-500"}>
                  {c.done ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                </button>
                <span className={`text-sm flex-1 ${c.done ? "line-through text-zinc-500" : "text-white"}`}>{c.text}</span>
                <button onClick={() => removeCheck(c.id)} className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-[#EF4444]">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <input
              value={newCheck}
              onChange={(e) => setNewCheck(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addChecklistItem()}
              placeholder="Add checklist item…"
              data-testid="panel-checklist-input"
              className="flex-1 bg-[#141417] border border-zinc-800 focus:border-zinc-600 rounded px-2 py-1.5 text-sm text-white outline-none"
            />
            <button onClick={addChecklistItem} className="bg-[#1B1B1F] hover:bg-[#27272A] border border-zinc-800 px-2 rounded text-zinc-300">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Subtasks */}
        <div className="px-5 py-4 border-b border-zinc-800">
          <div className="text-[10px] uppercase font-mono tracking-widest text-zinc-500 mb-2">
            Subtasks ({subtasks.length})
          </div>
          <div className="space-y-1.5" data-testid="panel-subtasks">
            {subtasks.map((s) => (
              <div key={s.id} className="flex items-center gap-2 px-2.5 py-1.5 bg-[#141417] border border-zinc-800 rounded">
                <span className={s.status === "done" ? "text-[#10B981]" : "text-zinc-500"}>
                  {s.status === "done" ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                </span>
                <span className={`text-sm flex-1 ${s.status === "done" ? "line-through text-zinc-500" : "text-zinc-200"}`}>{s.title}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <input
              value={newSub}
              onChange={(e) => setNewSub(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addSubtask()}
              placeholder="Add subtask…"
              data-testid="panel-subtask-input"
              className="flex-1 bg-[#141417] border border-zinc-800 focus:border-zinc-600 rounded px-2 py-1.5 text-sm text-white outline-none"
            />
            <button onClick={addSubtask} className="bg-[#1B1B1F] hover:bg-[#27272A] border border-zinc-800 px-2 rounded text-zinc-300">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Attachments */}
        <div className="px-5 py-4 border-b border-zinc-800" data-testid="panel-attachments">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase font-mono tracking-widest text-zinc-500 flex items-center gap-1.5">
              <Paperclip className="w-3 h-3" /> Attachments ({(task.attachments || []).length}/{MAX_ATTACHMENTS})
            </div>
            <button
              onClick={pickFile}
              disabled={attachUploading || (task.attachments || []).length >= MAX_ATTACHMENTS}
              data-testid="panel-attachment-add-btn"
              className="text-[11px] font-mono uppercase tracking-wider text-[#D4A968] hover:text-[#E8C285] flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {attachUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
              {attachUploading ? "Uploading…" : "Add file"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              data-testid="panel-attachment-input"
              onChange={uploadAttachment}
              accept=".jpg,.jpeg,.png,.webp,.gif,.heic,.heif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.md,.rtf"
            />
          </div>
          {attachError && (
            <div className="flex items-center gap-1.5 text-[11px] text-[#EF4444] mb-2" data-testid="panel-attachment-error">
              <AlertCircle className="w-3 h-3" /> {attachError}
            </div>
          )}
          {(task.attachments || []).length === 0 ? (
            <div className="text-xs text-zinc-600 italic">
              No files yet. Up to {MAX_ATTACHMENTS} files, {MAX_ATTACHMENT_MB} MB each.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {(task.attachments || []).map((a) => {
                const isImg = IMAGE_EXTS.has(_extOf(a.original_filename));
                const Icon = isImg ? ImageIcon : FileText;
                const href = `${API}${a.url.replace(/^\/api/, "")}`;
                return (
                  <li
                    key={a.id}
                    data-testid={`panel-attachment-row-${a.id}`}
                    className="flex items-center gap-2 px-2.5 py-1.5 bg-[#141417] border border-zinc-800 rounded group"
                  >
                    <Icon className="w-4 h-4 text-zinc-500 shrink-0" />
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-zinc-200 hover:text-[#D4A968] truncate flex-1 min-w-0"
                      title={a.original_filename}
                    >
                      {a.original_filename}
                    </a>
                    <span className="text-[10px] font-mono text-zinc-500 shrink-0">{_fmtSize(a.size)}</span>
                    <a
                      href={href}
                      download={a.original_filename}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-zinc-200 shrink-0"
                      title="Download"
                      data-testid={`panel-attachment-download-${a.id}`}
                    >
                      <Download className="w-3.5 h-3.5" />
                    </a>
                    <button
                      onClick={() => removeAttachment(a.id)}
                      className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-[#EF4444] shrink-0"
                      title="Delete"
                      data-testid={`panel-attachment-delete-${a.id}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* AI Summary */}
        <div className="px-5 py-4 border-b border-zinc-800">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase font-mono tracking-widest text-zinc-500 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3 text-[#D4A968]" /> AI Summary
            </div>
            <button
              onClick={generateSummary}
              disabled={aiLoading}
              data-testid="panel-ai-summary-btn"
              className="text-[11px] font-mono uppercase tracking-wider text-[#D4A968] hover:text-[#E8C285] flex items-center gap-1 disabled:opacity-50"
            >
              {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {aiLoading ? "Generating…" : "Generate"}
            </button>
          </div>
          {aiSummary ? (
            <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-text bg-[#141417] border border-[#D4A968]/30 rounded p-3" data-testid="panel-ai-summary-output">
{aiSummary}
            </pre>
          ) : (
            <div className="text-xs text-zinc-600 italic">Click Generate to summarise this task with GPT-5.2.</div>
          )}
        </div>

        {/* Comments */}
        <div className="px-5 py-4">
          <div className="text-[10px] uppercase font-mono tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
            <MessageCircle className="w-3 h-3" /> Comments ({comments.length})
          </div>
          <div className="space-y-2 mb-3" data-testid="panel-comments">
            {comments.map((c) => {
              const u = memberById(c.user_id);
              return (
                <div key={c.id} className="flex items-start gap-2">
                  <Avatar user={u || { name: "?" }} size="sm" />
                  <div className="flex-1 bg-[#141417] border border-zinc-800 rounded px-3 py-2">
                    <div className="text-xs text-zinc-400">
                      <span className="text-white font-medium">{u?.name || "Unknown"}</span>
                      <span className="ml-2 font-mono">{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                    <div className="text-sm text-zinc-200 mt-1">{c.text}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-start gap-2">
            <Avatar user={user} size="sm" />
            <div className="flex-1 flex gap-2">
              <input
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addComment()}
                placeholder="Add a comment… use @ to mention"
                data-testid="panel-comment-input"
                className="flex-1 bg-[#141417] border border-zinc-800 focus:border-zinc-600 rounded px-2.5 py-2 text-sm text-white outline-none"
              />
              <button onClick={addComment} data-testid="panel-comment-submit" className="bg-[#D4A968] hover:bg-[#E8C285] text-black px-3 rounded text-xs font-bold">
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
